<script lang="ts" setup>
import CartAside from '~/components/cart/CartAside.vue'
import CartEmpty from '~/components/cart/CartEmpty.vue'
import CartFooterSticky from '~/components/cart/CartFooterSticky.vue'
import CartNotifications from '~/components/cart/CartNotifications.vue'
import CartProducts from '~/components/cart/CartProducts.vue'
import CartTitle from '~/components/cart/CartTitle.vue'
import CartWidget from '~/components/cart/CartWidget.vue'
import ShareListButton from '~/components/cart/ShareListButton.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import { MindboxOperation } from '~/enums/mindbox-operation'
import OrderShippingType from '~/enums/order-shipping-type'
import Page from '~/enums/page'
import YandexMetrikaGoal from '~/enums/yandex-metrika-goal'
import formatter from '~/helpers/formatter'
import YandexMetrikaService from '~/services/yandex-metrika-service'
import { useAuthStore } from '~/store/auth'
import { useCartStore } from '~/store/cart'

const title = 'Корзина'

const authStore = useAuthStore()
const cartStore = useCartStore()

cartStore.initialPage = Page.Cart
cartStore.isDataLoading = true

const primaryPriceInKopecks = computed(() => {
  if (cartStore.minOrderSubtotalAmountInKopecksForFreeShipping === null) {
    return null
  }

  return Math.max(
    0,
    Math.min(
      cartStore.minOrderSubtotalAmountInKopecksForFreeShipping,
      cartStore.minOrderSubtotalAmountInKopecksForFreeShipping - cartStore.totalInKopecks
    )
  )
})

watch(
  () => cartStore.orderShippingType,
  (): void => {
    cartStore.shippingData = null
    cartStore.shippingCostInKopecks = cartStore.cartOrderShippingType?.shippingCostInKopecks ?? null
  }
)
watch(
  () => cartStore.subtotalWithDiscountInKopecks,
  (): void => {
    if (cartStore.orderShippingType === null) {
      return
    }

    if (OrderShippingType.hasShippingData(cartStore.orderShippingType)) {
      cartStore.shippingCostInKopecks = null
      cartStore.shippingData = null
    }
  }
)

useHead({ title: `${title} — iTAB` })

onMounted(async (): Promise<void> => {
  YandexMetrikaService.sendMetricEvent(YandexMetrikaGoal.ViewCart)

  // cartStore.$reset()

  const promoCode = useCookie('promoCode').value

  if (promoCode) {
    cartStore.form.promoCodeName = promoCode
  }

  if (authStore.isUser || authStore.isGuest) {
    if (promoCode) {
      cartStore.isPromoCodeApplying = true
    }

    await cartStore.fetchCartProducts()

    if (promoCode) {
      cartStore.isPromoCodeApplying = false
    }

    cartStore.trackCartByEcommerce(Page.Cart)
  }

  cartStore.isDataLoading = false
})
</script>

<template>
  <div>
    <Preloader v-if="cartStore.isDataLoading" />
    <div
      v-else-if="cartStore.cartProductsData.length"
      :class="{ 'pointer-events-none opacity-50': cartStore.isDataLoading2 }"
    >
      <section>
        <div class="container">
          <div class="flex w-full flex-col justify-between md:flex-row">
            <div class="w-full md:grow md:pr-5">
              <div class="mb-3 flex w-full items-center justify-between">
                <CartTitle :title="title" />

                <ShareListButton v-if="!authStore.isGuest && cartStore.cartProductsData.length > 0" />
              </div>

              <CartNotifications />

              <CartProducts />

              <div class="mt-3 flex flex-wrap items-stretch">
                <CartWidget
                  v-if="
                    cartStore.minOrderSubtotalAmountInKopecksForFreeShipping
                      && primaryPriceInKopecks !== null
                      && primaryPriceInKopecks > 0
                  "
                  class="mb-2 w-full bg-white lg:mb-0 lg:mr-[9px] lg:w-[calc(50%-4.5px)]"
                >
                  <div class="flex h-full items-center justify-between">
                    <div>
                      <strong class="text-[13px] font-medium leading-tight text-[#434242] lg:max-w-[67%]">
                        Бесплатная доставка до&nbsp;ПВЗ при&nbsp;заказ&nbsp;от&nbsp;{{ formatter.formatPrice(cartStore.minOrderSubtotalAmountInKopecksForFreeShipping, true) }}&nbsp;₽
                      </strong>

                      <div class="mt-2 text-[13px] leading-tight">
                        <p v-if="cartStore.isFreeShippingEnabled">
                          <span class="text-[#434242] opacity-[.5]">Добавьте товаров на&nbsp;</span>
                          <strong class="font-medium text-main-green">{{ formatter.formatPrice(primaryPriceInKopecks) }}&nbsp;₽</strong>
                        </p>
                        <p v-else>
                          У&nbsp;вас бесплатная доставка!
                        </p>
                      </div>
                    </div>

                    <ProgressCircle
                      :max-progress="cartStore.minOrderSubtotalAmountInKopecksForFreeShipping"
                      :progress="cartStore.subtotalWithDiscountInKopecks / 100"
                    />
                  </div>
                </CartWidget>

                <CartWidget v-if="false" class="w-full bg-white lg:w-[calc(50%-4.5px)]">
                  <div class="flex items-center justify-between">
                    <div class="mr-2">
                      <strong class="block text-[13px] font-medium text-[#434242]">
                        Подарим сертификат на&nbsp;3000&nbsp;₽&nbsp;на&nbsp;день отдыха в&nbsp;санатории за&nbsp;первый заказ&nbsp;от&nbsp;3000&nbsp;₽!
                      </strong>

                      <a
                        class="mt-2 block text-[13px] leading-tight text-main-green"
                        href="https://itab.pro/special-offers/podarok-za-registratsiyu-i-pervyy-zakaz-ot-3000-rub-den-v-sanatorii-11/"
                        target="_blank"
                      >Подробнее</a>
                    </div>

                    <SvgIcon class="!size-[42px]" name="outlined/success-rounded" />
                  </div>
                </CartWidget>
              </div>
            </div>

            <CartAside
              id="anchor3"
              class="mt-5 h-min w-full max-w-full md:mt-0 md:max-w-[315px] lg:max-w-[375px]"
              :page="Page.Cart"
            />
          </div>
        </div>
      </section>

      <MindboxRecommendationsSection
        v-if="cartStore.cartProductsData.length"
        class="my-10"
        :data="{ recommendation: { limit: 12 } }"
        :operation="MindboxOperation.RelatedProductsForCart"
        :title="
          cartStore.cartProductsData.length === 1
            ? 'С\xA0этим товаром покупают'
            : 'С\xA0этими товарами покупают'
        "
      />
    </div>
    <CartEmpty v-else />
    <CartFooterSticky />
  </div>
</template>
